// src/components/BackHandler.js
//
// Fixes the Android TWA back-button behaviour.
//
// Problem: In a Trusted Web Activity the hardware back button fires the
// browser's history.back(). If there is nothing left in the stack the
// Chrome Custom Tab (and therefore the app) closes immediately.
//
// Fix: We keep a sentinel state on top of the browser history at all
// times. When the back button is pressed it pops our sentinel (not a real
// route), popstate fires, and we decide what to do:
//
//   • Interior page → navigate(-1) via React Router (goes to previous page)
//   • Homepage, first press → re-push sentinel + show "press again to exit" toast
//   • Homepage, second press within 2 s → don't re-push → TWA exits cleanly
//
// Desktop web is unaffected: the same logic applies and works correctly with
// the browser back button too.
//
// Must be rendered INSIDE <Router> (uses useLocation / useNavigate).

import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const DOUBLE_TAP_MS = 2000;

const BackHandler = () => {
  const location  = useLocation();
  const navigate  = useNavigate();
  const [showToast, setShowToast] = useState(false);

  // Timestamp of the first back-press on the homepage (for double-tap-to-exit)
  const lastBackRef = useRef(null);
  // Timer handle for auto-hiding the toast
  const toastTimerRef = useRef(null);
  // Set to true before we call navigate(-1) so we can ignore the resulting popstate
  const ignoreNextPopRef = useRef(false);
  // Set to true before navigate(-1) so the location effect skips pushing a new
  // sentinel (the previous page's sentinel is already in the stack from when the
  // user first navigated there).
  const skipSentinelRef = useRef(false);

  // ─── Sentinel management ──────────────────────────────────────────────────
  // Push a sentinel (same-URL dummy state) on every route change so there is
  // always something to pop before a real history entry is lost.
  // Skipped after our own navigate(-1) call to avoid accumulating extra entries.
  useEffect(() => {
    if (skipSentinelRef.current) {
      skipSentinelRef.current = false;
      return;
    }
    window.history.pushState({ twaBack: true }, '', window.location.href);
  }, [location.pathname, location.search]);

  // ─── popstate handler ─────────────────────────────────────────────────────
  useEffect(() => {
    const handlePopState = () => {
      // Ignore the popstate that fires as a side-effect of our own navigate(-1)
      if (ignoreNextPopRef.current) {
        ignoreNextPopRef.current = false;
        return;
      }

      const isHome = location.pathname === '/';

      if (isHome) {
        const now = Date.now();
        if (lastBackRef.current && now - lastBackRef.current < DOUBLE_TAP_MS) {
          // Second back press within the window — allow the TWA to exit by NOT
          // re-pushing a sentinel. The browser history is now empty (or down to
          // the real entry) so Chrome Custom Tab will close the app.
          return;
        }

        // First back press on homepage: block the exit, show toast, reset timer
        lastBackRef.current = now;
        window.history.pushState({ twaBack: true }, '', window.location.href);

        setShowToast(true);
        clearTimeout(toastTimerRef.current);
        toastTimerRef.current = setTimeout(() => {
          setShowToast(false);
          lastBackRef.current = null;
        }, DOUBLE_TAP_MS);
      } else {
        // Interior page — navigate back within the React app.
        // ignoreNextPopRef: suppress the popstate from window.history.go(-1)
        //   that navigate(-1) triggers internally.
        // skipSentinelRef: tell the location effect NOT to push a fresh sentinel
        //   for the page we land on (the original sentinel is still in the stack).
        ignoreNextPopRef.current = true;
        skipSentinelRef.current  = true;
        navigate(-1);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [location.pathname, navigate]);

  // Cleanup on unmount
  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  if (!showToast) return null;

  return (
    <div className="back-exit-toast" role="status" aria-live="polite">
      Press back again to exit
    </div>
  );
};

export default BackHandler;
